/**
 * The in-app training-video catalog.
 *
 * Reference data, read straight out of `shared/config/training_videos.config.json`
 * (the same way `GoogleMapView` reads `app.config.json`). That file carries only
 * the module binding and the i18n keys; the cuts themselves are published by the
 * marketing site — see `videos/tutorials/PLAYBOOK.md` § 7 — and streamed from it,
 * so nothing here mints a path by hand beyond the published naming convention.
 *
 * `tests/unit/test_training_video_catalog.py` holds this file's config honest
 * against `www.panluma.ai/src/data/tutorials.json` and the `videos/tutorials/`
 * tree, so a slug, a language or a duration cannot drift between the two lists.
 */
import rawCatalog from '../../../../../shared/config/training_videos.config.json';

/** One produced cut of a tutorial, in one language. */
export interface TrainingVideoCut {
  language: string;
  durationSeconds: number;
}

export interface TrainingVideo {
  /** Matches the produced cut's directory under `videos/tutorials/`. */
  slug: string;
  /** The `ModuleConfig.id` this video teaches. */
  module: string;
  titleKey: string;
  descriptionKey: string;
  /** ISO date the cut was published. Part of the dismissal key, so a re-cut re-surfaces the tile. */
  publishedOn: string;
  cuts: TrainingVideoCut[];
}

interface TrainingVideoCatalog {
  baseUrl: string;
  libraryUrl: string;
  videos: TrainingVideo[];
}

const catalog = rawCatalog as unknown as TrainingVideoCatalog;

/**
 * The language every tutorial is authored in, and therefore the fallback for a
 * UI language no cut exists in. `videos/tutorials/PLAYBOOK.md` § 5: English is
 * the master text and every other language is a translation of it.
 */
const MASTER_LANGUAGE = 'en';

export const TRAINING_VIDEOS: readonly TrainingVideo[] = catalog.videos;

/** Every video, in catalog order. */
export function allTrainingVideos(): readonly TrainingVideo[] {
  return catalog.videos;
}

/** The video that teaches `moduleId`, or undefined — most modules have none yet. */
export function trainingVideoForModule(moduleId: string | undefined): TrainingVideo | undefined {
  if (!moduleId) return undefined;
  return catalog.videos.find((video) => video.module === moduleId);
}

/**
 * The cut to play for a viewer reading the UI in `uiLanguage`.
 *
 * A cut exists in the viewer's language, or it does not and they get the English
 * master. The returned cut names the language it actually IS, so the caller can
 * label the fallback rather than let a Spanish user press play on a video they
 * were not told is in English. Returns undefined only if the entry has no cuts
 * at all, which the catalog test forbids.
 */
export function resolveCut(video: TrainingVideo, uiLanguage: string): TrainingVideoCut | undefined {
  // `i18next` hands back tags like `en-US`; the catalog is keyed by the base tag.
  const base = uiLanguage.split('-')[0];
  return (
    video.cuts.find((cut) => cut.language === base) ??
    video.cuts.find((cut) => cut.language === MASTER_LANGUAGE) ??
    video.cuts[0]
  );
}

/** True when the viewer is being offered a cut in a language they did not ask for. */
export function isFallbackCut(cut: TrainingVideoCut, uiLanguage: string): boolean {
  return cut.language !== uiLanguage.split('-')[0];
}

/** The published mp4. Naming convention: `www.panluma.ai/VIDEO-ASSETS.md`. */
export function videoUrl(video: TrainingVideo, cut: TrainingVideoCut): string {
  return `${catalog.baseUrl}/${video.slug}.${cut.language}.mp4`;
}

/** The WebVTT captions the site's prebuild converts from the produced `.srt`. */
export function captionsUrl(video: TrainingVideo, cut: TrainingVideoCut): string {
  return `${catalog.baseUrl}/${video.slug}.${cut.language}.vtt`;
}

/** The cut's own title card, used as the poster so nothing downloads before play. */
export function posterUrl(video: TrainingVideo): string {
  return `${catalog.baseUrl}/${video.slug}.poster.png`;
}

/**
 * The public library page for this cut — where the player sends someone whose
 * browser could not play the stream, and where the Help menu links for the rest
 * of the library.
 */
export function libraryPageUrl(video: TrainingVideo, cut: TrainingVideoCut): string {
  return `${catalog.libraryUrl}/${video.slug}/${cut.language}`;
}

export function libraryUrl(): string {
  return catalog.libraryUrl;
}
